import { addDays, describeDue, dueBucket, localDay } from '@quickreport/domain';
import { Button, Input, useDismiss } from '@quickreport/ui';
import { completeReminder, setDueDate } from '@quickreport/workspace';
import { AlarmClock, Check } from 'lucide-react';
import { useCallback, useState } from 'react';
import { describeError } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { useCtx } from './context';

/**
 * When the report is promised. A button in the report's header shows it; opening it lets you
 * move the date or mark the work complete. A signed report just shows the day.
 */
export function DueControl({
  reportId,
  dueDate,
  dueDoneAt,
  status,
}: {
  reportId: string;
  dueDate: string | null;
  dueDoneAt: string | null;
  status: string;
}) {
  const ws = useWorkspace();
  const ctx = useCtx();
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState('');
  const [error, setError] = useState<string | null>(null);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  const today = localDay();

  if (!dueDate) return null;
  const finished = status === 'final' || status === 'revoked';
  const bucket = dueBucket(dueDate, today);
  const label = dueDoneAt
    ? 'Marked complete'
    : finished
      ? `Was due ${describeDue(dueDate, today)}`
      : bucket === 'overdue'
        ? `Overdue: due ${describeDue(dueDate, today)}`
        : `Due ${describeDue(dueDate, today)}`;
  const tone =
    dueDoneAt || finished
      ? 'text-slate'
      : bucket === 'overdue'
        ? 'text-flag'
        : bucket === 'today'
          ? 'text-warn'
          : 'text-slate';
  if (finished)
    return (
      <span className={`inline-flex items-center gap-1 ${tone}`}>
        <AlarmClock size={12} /> {label}
      </span>
    );

  async function run(fn: () => Promise<void>) {
    setError(null);
    try {
      await fn();
      void ws.engine.syncNow();
      setOpen(false);
    } catch (e) {
      setError(describeError(e));
    }
  }

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setDay(dueDate);
          setOpen((o) => !o);
        }}
        className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:bg-canvas ${tone}`}
        title="Change the due date"
      >
        <AlarmClock size={12} /> {label}
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Due date"
          className="absolute left-0 z-30 mt-1 grid w-64 gap-2 rounded-xl border border-line bg-paper p-3 text-[13px] text-ink shadow-lg"
        >
          <div className="font-medium">Report due</div>
          <Input
            type="date"
            aria-label="Due date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
          />
          <div className="flex flex-wrap gap-1.5">
            {(
              [
                ['Today', 0],
                ['Tomorrow', 1],
                ['In 2 days', 2],
              ] as const
            ).map(([l, n]) => (
              <Button key={l} size="sm" onClick={() => setDay(addDays(today, n))}>
                {l}
              </Button>
            ))}
          </div>
          {error ? (
            <p role="alert" className="text-flag">
              {error}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button
              tone="primary"
              size="sm"
              disabled={!day || day === dueDate}
              onClick={() => void run(() => setDueDate(ws.db, ctx, reportId, day))}
            >
              Save date
            </Button>
            {!dueDoneAt ? (
              <Button
                size="sm"
                onClick={() => void run(() => completeReminder(ws.db, ctx, reportId))}
              >
                <Check size={13} /> Mark complete
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
